import React from "react";
import "./PackingListDocumentView.css"; // shared 5999Cargo document styles
import { ShieldCheck, ShieldAlert, Truck } from "lucide-react";
import EmailActivityPanel from "../EmailActivityPanel";
import BookingDocumentSetPanel from "./BookingDocumentSetPanel";
import { computeLineCbm, computeTotals } from "./BookingItemsEditor";
import { useAuth } from "../../../context/AuthContext";
import LifecycleStatusBadge from "./LifecycleStatusBadge";
import LifecycleStatusBanner from "./LifecycleStatusBanner";
import DocumentLifecycleActions from "./DocumentLifecycleActions";
import DocumentVersionTimeline from "./DocumentVersionTimeline";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL"); } catch { return iso; }
}
function fmtEur(n) {
  const v = Number(n || 0);
  return v.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
}
function joinAddr(...parts) {
  return parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");
}

function Field({ label, children, testid }) {
  return (
    <div className="pkl-field" data-testid={testid}>
      <div className="pkl-field__label">{label}</div>
      <div className="pkl-field__value">{children || "—"}</div>
    </div>
  );
}

export default function BookingFormDocumentView({ pkl: bf, onLifecycleChanged }) {
  const { hasPermission, user } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const canGenerate =
    hasPermission("orders:convert_financial") ||
    ["owner", "admin", "manager"].includes(userRole);
  if (!bf) return null;
  const lifecycleStatus = bf.lifecycle_status || null;
  const showHistoricalBanner = ["vervangen", "duplicaat", "geannuleerd"].includes(lifecycleStatus);

  const items = bf.items || [];
  const totals = items.length ? computeTotals(items) : null;
  const currency = bf.value_invoice_currency || "EUR";

  const senderName =
    [bf.first_name, bf.last_name].filter(Boolean).join(" ").trim() || "—";
  const senderAddr = joinAddr(
    bf.sender_address,
    [bf.sender_postcode, bf.sender_city].filter(Boolean).join(" "),
    bf.sender_country,
  );
  const recvAddr = joinAddr(bf.recipient_address, bf.recipient_city, bf.recipient_country);
  const bidShort = (bf.id || "").slice(0, 8).toUpperCase() || "—";
  const dgOk = !!bf.dangerous_goods_confirmed;

  return (
    <article className="pkl-doc" data-testid="bf-doc">
      {/* Fase 2B — Lifecycle status + acties + waarschuwing */}
      {lifecycleStatus && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <LifecycleStatusBadge status={lifecycleStatus} />
            {bf.version_number && (
              <span className="text-xs text-gray-600">
                Versie {bf.version_number}
              </span>
            )}
          </div>
          <DocumentLifecycleActions
            docType="booking_form"
            docId={bf.id}
            lifecycleStatus={lifecycleStatus}
            onChanged={onLifecycleChanged}
          />
        </div>
      )}
      {showHistoricalBanner && (
        <LifecycleStatusBanner
          status={lifecycleStatus}
          reason={bf.lifecycle_reason}
          replacedBy={bf.superseded_by_document_id}
          replacesId={bf.replaces_document_id}
        />
      )}

      {/* Header */}
      <header className="pkl-doc__header">
        <h1 className="pkl-doc__title">BOEKINGSFORMULIER</h1>
        <p className="pkl-doc__brand">
          5999Cargo Group Europe — Booking form / shipment request
        </p>
      </header>

      {/* Meta ribbon */}
      <div className="pkl-doc__meta">
        <div className="pkl-doc__meta-cell">
          <div className="label">Boekingsnr.</div>
          <div className="val" data-testid="bf-doc-id">{bidShort}</div>
        </div>
        <div className="pkl-doc__meta-cell">
          <div className="label">Datum</div>
          <div className="val">{fmtDate(bf.created_at)}</div>
        </div>
        <div className="pkl-doc__meta-cell">
          <div className="label">Bestemming</div>
          <div className="val">{bf.destination || "—"}</div>
        </div>
        <div className="pkl-doc__meta-cell">
          <div className="label">Waarde goederen</div>
          <div className="val" data-testid="bf-doc-value">{fmtEur(bf.goods_value)}</div>
        </div>
      </div>

      {/* Body */}
      <div className="pkl-doc__body">
        {/* Sender + Receiver */}
        <div className="pkl-grid-2">
          <section className="pkl-card" data-testid="bf-doc-sender">
            <h3 className="pkl-card__title">Verzender</h3>
            <div className="pkl-card__body">
              <Field label="Naam">{senderName}</Field>
              {bf.company_name && <Field label="Bedrijf">{bf.company_name}</Field>}
              <Field label="E-mail">{bf.email}</Field>
              <Field label="Telefoon">{bf.phone}</Field>
              {bf.whatsapp && <Field label="WhatsApp">{bf.whatsapp}</Field>}
              <Field label="Adres">{senderAddr}</Field>
            </div>
          </section>

          <section className="pkl-card" data-testid="bf-doc-receiver">
            <h3 className="pkl-card__title">Ontvanger</h3>
            <div className="pkl-card__body">
              <Field label="Naam">{bf.recipient_name}</Field>
              {bf.recipient_company && <Field label="Bedrijf">{bf.recipient_company}</Field>}
              {bf.recipient_email && <Field label="E-mail">{bf.recipient_email}</Field>}
              <Field label="Telefoon">{bf.recipient_phone}</Field>
              <Field label="Adres">{recvAddr}</Field>
              {bf.recipient_instructions && (
                <Field label="Instructies">
                  <div style={{ whiteSpace: "pre-wrap" }}>{bf.recipient_instructions}</div>
                </Field>
              )}
            </div>
          </section>
        </div>

        {/* Shipment */}
        <section className="pkl-card" data-testid="bf-doc-shipment">
          <h3 className="pkl-card__title">Zendinggegevens</h3>
          <div className="pkl-card__body">
            <div className="pkl-grid-2">
              <div>
                <Field label="Bestemming">{bf.destination}</Field>
                <Field label="Type">{bf.shipment_type}</Field>
                <Field label="Methode">{bf.method}</Field>
                <Field label="Dozen">{bf.boxes ?? "—"}</Field>
              </div>
              <div>
                <Field label="Pallets">{bf.pallets ?? "—"}</Field>
                <Field label="Gewicht (kg)">{bf.estimated_weight ?? "—"}</Field>
                <Field label="CBM">{bf.estimated_cbm ?? "—"}</Field>
                <Field label="Waarde goederen">{fmtEur(bf.goods_value)}</Field>
              </div>
            </div>

            {bf.goods_description && (
              <div style={{ marginTop: 12 }}>
                <Field label="Omschrijving">
                  <div style={{ whiteSpace: "pre-wrap" }}>{bf.goods_description}</div>
                </Field>
              </div>
            )}

            {/* Sprint 2 — items tabel wanneer aanwezig */}
            {items.length > 0 && (
              <div style={{ marginTop: 16 }} data-testid="bf-doc-items-table">
                <div className="pkl-field__label" style={{ marginBottom: 6 }}>
                  Items / Colli ({items.length})
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f7f8fb", textAlign: "left" }}>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6" }}>Omschrijving</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", textAlign: "right" }}>Aantal</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", textAlign: "right" }}>L</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", textAlign: "right" }}>B</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", textAlign: "right" }}>H</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6" }}>Eenh.</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", textAlign: "right" }}>CBM</th>
                        {bf.wants_value_invoice && (
                          <th style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", textAlign: "right" }}>
                            Waarde/stuk ({currency})
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} data-testid={`bf-doc-item-${i}`}>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4" }}>
                            <div>{it.description || "—"}</div>
                            {(it.condition || it.note) && (
                              <div style={{ fontSize: 11, color: "#6b7280" }}>
                                {[it.condition, it.note].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4", textAlign: "right" }}>
                            {it.quantity}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4", textAlign: "right" }}>
                            {it.length}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4", textAlign: "right" }}>
                            {it.width}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4", textAlign: "right" }}>
                            {it.height}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4" }}>
                            {it.unit || "cm"}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4", textAlign: "right", fontFamily: "monospace" }}>
                            {computeLineCbm(it).toFixed(4)}
                          </td>
                          {bf.wants_value_invoice && (
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #eef0f4", textAlign: "right", fontFamily: "monospace" }}>
                              {(Number(it.value_per_unit) || 0).toFixed(2)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: "#0A192F", color: "#fff", fontWeight: 700 }}>
                        <td style={{ padding: "6px 8px" }}>Totaal</td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>{totals.pieces}</td>
                        <td colSpan={4}></td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>
                          {totals.cbm.toFixed(4)}
                        </td>
                        {bf.wants_value_invoice && (
                          <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>
                            {(totals.value || 0).toFixed(2)} {currency}
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {totals.weight > 0 && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    Totaal gewicht: {totals.weight.toFixed(1)} kg
                  </div>
                )}
              </div>
            )}

            {/* Dangerous goods badge */}
            <div style={{ marginTop: 14 }}>
              {dgOk ? (
                <span className="pkl-badge pkl-badge--ok" data-testid="bf-doc-dg-ok">
                  <ShieldCheck className="w-4 h-4" />
                  Bevestigd — geen verboden goederen / Dangerous Goods opgegeven
                </span>
              ) : (
                <span className="pkl-badge pkl-badge--warn" data-testid="bf-doc-dg-warn">
                  <ShieldAlert className="w-4 h-4" />
                  Niet bevestigd — afzender heeft de Dangerous-Goods verklaring NIET afgegeven
                </span>
              )}
              {bf.dangerous_goods_confirmed_at && (
                <div style={{ fontSize: 12, color: "#514e4e", marginTop: 4 }}>
                  Bevestigd op: {fmtDateTime(bf.dangerous_goods_confirmed_at)}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Pickup — only when requested */}
        {bf.pickup_required && (
          <section className="pkl-card" data-testid="bf-doc-pickup">
            <h3 className="pkl-card__title">
              <Truck className="w-3.5 h-3.5 inline mr-1" style={{ verticalAlign: "-2px" }} />
              Pickupgegevens
            </h3>
            <div className="pkl-card__body">
              <Field label="Pickup">Ja, op afgesproken locatie</Field>
              <Field label="Adres">{bf.pickup_address}</Field>
              <Field label="Datum / tijd">{bf.pickup_datetime}</Field>
            </div>
          </section>
        )}

        {/* Sprint 3b — 6 Mrt 2026: Banner "klant-aanvraag documentenset" */}
        {bf.id && (() => {
          const requested = !!bf.wants_document_set;
          return (
            <div className="mb-3" data-testid="bf-doc-clientdocset-banner">
              <div
                className={`rounded-none border-l-4 p-3 ${
                  requested
                    ? "bg-emerald-50 border-emerald-500 text-emerald-900"
                    : "bg-gray-50 border-gray-300 text-gray-800"
                }`}
                data-testid={`bf-doc-clientdocset-banner-${requested ? "yes" : "no"}`}
              >
                <div className="font-chivo font-bold text-sm">
                  {requested
                    ? "Documentenset aangevraagd door klant"
                    : "Geen documentenset aangevraagd door klant"}
                </div>
                <div className={`text-xs mt-0.5 ${requested ? "text-emerald-800" : "text-gray-600"}`}>
                  {requested
                    ? "Boekingsformulier + Paklijst + Waardefactuur"
                    : "Backoffice kan alsnog handmatig documenten genereren indien nodig."}
                </div>
              </div>
              {/* Metadata-pills onder de banner (alleen als er data is) */}
              {(bf.value_invoice_currency || items.length > 0) && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="bf-doc-clientdocset-pills">
                  {bf.value_invoice_currency && (
                    <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 bg-white text-[11px] text-gray-700"
                          data-testid="bf-doc-pill-currency">
                      Valuta: <strong className="ml-1">{bf.value_invoice_currency}</strong>
                    </span>
                  )}
                  {items.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 bg-white text-[11px] text-gray-700"
                          data-testid="bf-doc-pill-items">
                      Items: <strong className="ml-1">{items.length}</strong>
                    </span>
                  )}
                  {totals && Number(totals.pieces || 0) > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 bg-white text-[11px] text-gray-700"
                          data-testid="bf-doc-pill-pieces">
                      Aantal: <strong className="ml-1 font-mono">{Math.round(totals.pieces)}</strong>
                    </span>
                  )}
                  {totals && Number(totals.cbm || 0) > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 bg-white text-[11px] text-gray-700"
                          data-testid="bf-doc-pill-cbm">
                      CBM: <strong className="ml-1 font-mono">{Number(totals.cbm).toFixed(4)}</strong>
                    </span>
                  )}
                  {totals && Number(totals.weight || 0) > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 bg-white text-[11px] text-gray-700"
                          data-testid="bf-doc-pill-weight">
                      Gewicht: <strong className="ml-1 font-mono">{Number(totals.weight).toFixed(1)} kg</strong>
                    </span>
                  )}
                  {totals && Number(totals.value || 0) > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 border border-gray-200 bg-white text-[11px] text-gray-700"
                          data-testid="bf-doc-pill-value">
                      Totale waarde: <strong className="ml-1 font-mono">
                        {Number(totals.value).toLocaleString("nl-NL", { style: "currency", currency: bf.value_invoice_currency || "EUR" })}
                      </strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Sprint 2 — Documentenset paneel */}
        {bf.id && (
          <BookingDocumentSetPanel
            bookingId={bf.id}
            canGenerate={canGenerate}
            refreshKey={`${bf.updated_at || ""}::${items.length}`}
          />
        )}

        {/* Klant-akkoord */}
        <section className="pkl-card" data-testid="bf-doc-consents">
          <h3 className="pkl-card__title">Klant-akkoord</h3>
          <div className="pkl-card__body">
            <Field label="Waarheid">
              {bf.truthful_data ? "✓ Verklaring naar waarheid ingevuld" : "✗ Niet bevestigd"}
            </Field>
            <Field label="Contact">
              {bf.consent_contact ? "✓ Toestemming voor contact" : "✗ Niet bevestigd"}
            </Field>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="pkl-doc__disclaimer" data-testid="bf-doc-disclaimer">
          <div className="heading">Verklaring van de afzender</div>
          <p style={{ margin: 0 }}>
            De afzender verklaart dat dit boekingsformulier naar waarheid is ingevuld en dat de
            zending geen verboden goederen of gevaarlijke stoffen bevat die zonder voorafgaande
            toestemming niet vervoerd mogen worden.
          </p>
          <p style={{ margin: "8px 0 0" }}>
            5999Cargo Group Europe is niet verantwoordelijk voor onjuist opgegeven inhoud, waarde
            of ontbrekende documenten. Definitieve tarieven kunnen wijzigen na controle, meting,
            weging, documentcontrole of douanevereisten.
          </p>
        </div>
      </div>
      {bf?.id && (
        <EmailActivityPanel refKind="booking_form" refId={bf.id} />
      )}
      {bf?.id && lifecycleStatus && (
        <section className="mt-4 border border-gray-200 p-3 bg-white">
          <h3 className="text-sm font-chivo font-bold text-[#0A192F] mb-2">
            Versiehistorie
          </h3>
          <DocumentVersionTimeline docType="booking_form" docId={bf.id} />
        </section>
      )}
    </article>
  );
}
